// src/components/fix-actions/redesign/optimization-studio-view.tsx
//
// Redesigned Optimization Studio tab body. Same data source as the legacy tab
// (useOptimizationStudio + optimistic merge), rendered as a status-led
// pipeline (list) or a kanban board. Status is mapped to the locked
// user-facing pipeline; "Verified"/"Deployed" collapse into "Published".

import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import type { StrategyArtifact } from "../../../services/fix-actions/optimization-studio-service";
import { useOptimizationStudio } from "../../../hooks/fix-actions/useOptimizationStudio";
import {
  statusLabel,
  statusVisual,
  statusPillCss,
  typeColor,
  PIPELINE_ORDER,
  Ic,
  SPARKLE,
  type PipelineLabel,
} from "./shared";
import { ExplainPopover, type ExplainAnchor } from "./explain-popover";
import { ACTION_ITEMS_PREVIEW, previewArtifacts } from "./preview";
import { ClaimCheckBadge } from "../artifact-claim-check";

const STUDIO_FILTERS: { k: string; label: string }[] = [
  { k: "all", label: "All assets" },
  { k: "Draft", label: "Drafts" },
  { k: "In Review", label: "In Review" },
  { k: "Ready to publish", label: "Ready to publish" },
  { k: "Published", label: "Published" },
];

export interface OptimizationStudioViewProps {
  onOpenArtifactTray: (artifact: StrategyArtifact) => void;
  onExplainWithAI?: (topic: string) => void;
  optimisticArtifacts?: StrategyArtifact[];
  brandId: string;
  highlightArtifactId?: string | null;
  onClearHighlight?: () => void;
}

export function OptimizationStudioView({
  onOpenArtifactTray,
  onExplainWithAI,
  optimisticArtifacts = [],
  brandId,
  highlightArtifactId,
  onClearHighlight,
}: OptimizationStudioViewProps) {
  const { data, loading, error } = useOptimizationStudio({ brandId });
  const [view, setView] = useState<"list" | "board">("list");
  const [isNarrow, setIsNarrow] = useState<boolean>(() => typeof window !== "undefined" && window.innerWidth < 860);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 860);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [filter, setFilter] = useState<string>("all");
  const [filterMenu, setFilterMenu] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [explain, setExplain] = useState<ExplainAnchor | null>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const merged: StrategyArtifact[] = useMemo(() => {
    const realBackend = data?.artifacts ?? [];
    // dev-only: when there's no real data, treat the sample assets as the
    // backend set so optimistic updates (e.g. opening one) merge by id
    // instead of collapsing the list.
    const backend = ACTION_ITEMS_PREVIEW && realBackend.length === 0 ? previewArtifacts() : realBackend;
    const optById = new Map(optimisticArtifacts.map((a) => [a.id, a]));
    const out: StrategyArtifact[] = backend.map((b) => (optById.has(b.id) ? optById.get(b.id)! : b));
    for (const opt of optimisticArtifacts) {
      if (!backend.some((b) => b.id === opt.id)) out.unshift(opt);
    }
    return out;
  }, [data, optimisticArtifacts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return merged.filter((a) => {
      const label = statusLabel(a.status);
      if (filter !== "all" && label !== filter) return false;
      if (q && !(a.name.toLowerCase().includes(q) || String(a.type).toLowerCase().includes(q) || (a.supportingActionTitle || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [merged, filter, search]);

  useEffect(() => {
    if (!highlightArtifactId) return;
    const t = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (onClearHighlight) setTimeout(onClearHighlight, 1500);
    }, 200);
    return () => clearTimeout(t);
  }, [highlightArtifactId, onClearHighlight]);

  const explainFrom = (type: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setExplain({ type, x: r.left, y: r.bottom + 8 });
  };

  const iconBtn = (active: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 30,
    borderRadius: 7,
    cursor: "pointer",
    border: `1px solid ${active ? "var(--alpha-teal-30, var(--alpha-teal))" : "var(--border)"}`,
    background: active ? "var(--alpha-teal-5)" : "var(--background)",
    color: active ? "var(--alpha-teal)" : "var(--muted-foreground)",
  });

  const filterLabel = STUDIO_FILTERS.find((o) => o.k === filter)?.label || "All assets";

  // ── artifact row (list) ──
  const artifactRow = (x: StrategyArtifact, hasBorder: boolean) => {
    const label = statusLabel(x.status);
    const isOpen = !!expanded[x.id];
    const isHi = x.id === highlightArtifactId;
    return (
      <div
        key={x.id}
        ref={isHi ? highlightRef : undefined}
        style={{ borderBottom: hasBorder ? "1px solid var(--border)" : "none", background: isHi ? "var(--alpha-teal-5)" : "var(--background)" }}
      >
        <div
          className="ai-row"
          onClick={() => setExpanded((s) => ({ ...s, [x.id]: !s[x.id] }))}
          style={{ display: "flex", alignItems: "center", gap: 13, padding: "12px 16px", cursor: "pointer" }}
        >
          <span title={label} style={{ display: "inline-flex", flexShrink: 0, width: 8, height: 8, borderRadius: 9999, background: statusVisual(label).bar }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)", lineHeight: 1.4, marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "var(--muted-foreground)" }}>
              <button onClick={(e) => explainFrom(String(x.type), e)} style={{ fontSize: 11.5, color: typeColor(String(x.type)), fontWeight: 500, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px", textDecorationStyle: "dotted" }}>{String(x.type)}</button>
              <span style={{ width: 3, height: 3, borderRadius: 9999, background: "var(--silver-mist)" }} />
              <span>{x.supportingActionTitle || `Action #${x.supportingActionId}`}</span>
            </div>
          </div>
          <span style={statusPillCss(label)}>{label}</span>
          <ClaimCheckBadge check={x.claimCheck} />
          <span style={{ display: "inline-flex", color: "var(--muted-foreground)", transition: "transform .2s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0 }}>
            <Ic paths={["m9 18 6-6-6-6"]} size={15} sw={2} />
          </span>
        </div>
        {isOpen && (
          <div style={{ padding: "2px 16px 16px 46px", borderTop: "1px solid var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "14px 0 12px", fontSize: 12 }}>
              {x.strategicTheme && (
                <span style={{ color: "var(--muted-foreground)" }}>
                  <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", fontSize: 11 }}>Theme: </span>
                  <span style={{ color: "var(--foreground)", fontWeight: 600 }}>{x.strategicTheme}</span>
                </span>
              )}
              {(x.deploymentTargets || []).length > 0 && (
                <>
                  <span style={{ width: 1, height: 14, background: "var(--border)" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)" }}>Deploy to:</span>
                  {x.deploymentTargets.map((d, i) => (
                    <span key={i} style={{ fontSize: 11, fontWeight: 500, color: "var(--muted-foreground)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 6, padding: "2px 8px" }}>{d}</span>
                  ))}
                </>
              )}
            </div>
            {x.intent && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", marginBottom: 6 }}>Strategic intent</div>
                <p style={{ fontSize: 13, color: "var(--foreground)", lineHeight: 1.55, margin: "0 0 16px" }}>{x.intent}</p>
              </>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenArtifactTray(x); }}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 8, border: "none", background: "var(--alpha-teal)", color: "var(--accent-foreground)", cursor: "pointer" }}
              >
                Open Asset
              </button>
              {onExplainWithAI && (
                <button
                  className="ai-chip"
                  onClick={(e) => { e.stopPropagation(); onExplainWithAI(`What does the asset "${x.name}" change?`); }}
                  style={{ display: "inline-flex", alignItems: "center", height: 34, padding: "0 14px", borderRadius: 9999, background: "var(--card)", border: "1px solid var(--border)", fontSize: 12, fontWeight: 500, color: "var(--foreground)", cursor: "pointer", fontFamily: "inherit" }}
                >
                  Ask Upmode about this
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── card (board) ──
  const card = (x: StrategyArtifact) => {
    const label = statusLabel(x.status);
    const isHi = x.id === highlightArtifactId;
    return (
      <div
        key={x.id}
        ref={isHi ? highlightRef : undefined}
        className="ai-row"
        onClick={() => onOpenArtifactTray(x)}
        style={{ border: "1px solid var(--border)", borderLeft: `3px solid ${statusVisual(label).bar}`, borderRadius: 12, padding: "16px 18px", background: isHi ? "var(--alpha-teal-5)" : "var(--card)", cursor: "pointer", boxShadow: "var(--elevation-sm)" }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--foreground)", lineHeight: 1.4, marginBottom: 8 }}>{x.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "var(--muted-foreground)" }}>
              <button onClick={(e) => explainFrom(String(x.type), e)} style={{ fontSize: 11.5, fontWeight: 600, color: typeColor(String(x.type)), background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px", textDecorationStyle: "dotted" }}>{String(x.type)}</button>
            </div>
          </div>
          <ClaimCheckBadge check={x.claimCheck} />
        </div>
      </div>
    );
  };

  // ── states ──
  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "96px 0" }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--signal-blue)" }} />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "96px 16px" }}>
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <AlertTriangle className="w-10 h-10" style={{ color: "var(--infrared-coral)", margin: "0 auto 16px" }} />
          <p style={{ color: "var(--muted-foreground)" }}>{(error as any)?.message || "Failed to load assets."}</p>
        </div>
      </div>
    );
  }
  if (merged.length === 0) {
    return (
      <div style={{ border: "1px dashed var(--silver-mist)", borderRadius: 16, background: "var(--muted)", padding: "52px 32px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <span style={{ width: 52, height: 52, borderRadius: 14, background: "var(--card)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted-foreground)", marginBottom: 18 }}>
          <Ic paths={SPARKLE} size={24} sw={1.6} />
        </span>
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--foreground)", marginBottom: 7 }}>No assets yet</div>
        <p style={{ fontSize: 13.5, color: "var(--muted-foreground)", lineHeight: 1.6, margin: 0, maxWidth: 400 }}>
          When you generate an optimization asset from a suggested action, it lands here as a draft you can review, refine, and publish.
        </p>
      </div>
    );
  }

  const boardCols: { key: PipelineLabel }[] = PIPELINE_ORDER.map((k) => ({ key: k }));
  const listGroups = PIPELINE_ORDER.map((st) => ({ st, items: filtered.filter((x) => statusLabel(x.status) === st) })).filter((g) => g.items.length);

  return (
    <div>
      {/* secondary bar */}
      <div style={{ padding: "14px 0 18px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--muted-foreground)" }}>{merged.length} assets</span>
        <div style={{ position: "relative" }}>
          <button
            onClick={(e) => { e.stopPropagation(); setFilterMenu((v) => !v); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 500, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            <Ic paths={["M3 6h18M7 12h10M11 18h2"]} size={14} sw={1.7} color="var(--muted-foreground)" />
            {filterLabel}
            <Ic paths={["m6 9 6 6 6-6"]} size={12} sw={2} />
          </button>
          {filterMenu && (
            <>
              <div onClick={() => setFilterMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, minWidth: 190, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--elevation-md)", padding: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", padding: "6px 10px 5px" }}>Show</div>
                {STUDIO_FILTERS.map((o) => {
                  const active = filter === o.k;
                  return (
                    <button
                      key={o.k}
                      onClick={() => { setFilter(o.k); setFilterMenu(false); }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", fontSize: 13, fontWeight: active ? 600 : 500, color: active ? "var(--alpha-teal)" : "var(--foreground)", background: active ? "var(--teal-pill-bg, var(--alpha-teal-10))" : "transparent", border: "none", borderRadius: 7, padding: "8px 10px", cursor: "pointer" }}
                    >
                      {o.label}
                      {active && <Ic paths={["m5 12 5 5L20 7"]} size={14} sw={2.4} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", display: "inline-flex" }}>
            <Ic paths={["M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0", "m21 21-4.3-4.3"]} size={15} sw={1.7} color="var(--muted-foreground)" />
          </span>
          <input
            className="ai-input"
            type="text"
            placeholder="Search assets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220, maxWidth: "46vw", padding: "8px 12px 8px 34px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--background)", fontSize: 13, fontFamily: "var(--font-sans)", color: "var(--foreground)", outline: "none" }}
          />
        </div>
        {!isNarrow && (
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            <button title="List view" onClick={() => setView("list")} style={iconBtn(view === "list")}>
              <Ic paths={["M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"]} size={15} sw={1.8} />
            </button>
            <button title="Board view" onClick={() => setView("board")} style={iconBtn(view === "board")}>
              <Ic paths={["M3 3h7v18H3z", "M14 3h7v18h-7z"]} size={15} sw={1.8} />
            </button>
          </div>
        )}
      </div>

      {!isNarrow && view === "board" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, alignItems: "start" }}>
          {boardCols.map((col) => {
            const items = filtered.filter((x) => statusLabel(x.status) === col.key);
            const v = statusVisual(col.key);
            return (
              <div key={col.key} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", background: v.bg, borderRadius: 10, borderTop: `2px solid ${v.bar}` }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: v.text }}>{col.key}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: v.text, background: "var(--card)", borderRadius: 9999, minWidth: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px" }}>{items.length}</span>
                </div>
                {items.map((x) => card(x))}
                {items.length === 0 && <div style={{ padding: "18px 0", textAlign: "center", fontSize: 12, color: "var(--muted-foreground)" }}>None</div>}
              </div>
            );
          })}
        </div>
      ) : listGroups.length === 0 ? (
        <div style={{ border: "1px dashed var(--silver-mist)", borderRadius: 16, background: "var(--muted)", padding: "40px", textAlign: "center", color: "var(--muted-foreground)", fontSize: 13.5 }}>
          No assets match this filter or search.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {listGroups.map((g) => (
            <div key={g.st}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 4px 10px" }}>
                <span style={{ width: 10, height: 10, borderRadius: 9999, background: statusVisual(g.st).bar, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>{g.st}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--muted-foreground)", background: "var(--muted)", borderRadius: 9999, minWidth: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px" }}>{g.items.length}</span>
              </div>
              <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", boxShadow: "var(--elevation-sm)" }}>
                {g.items.map((x, i) => artifactRow(x, i < g.items.length - 1))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ExplainPopover anchor={explain} onClose={() => setExplain(null)} />
    </div>
  );
}
